'use client';

/**
 * VolumeByMuscleCard — weekly working sets per muscle group classified
 * against MV / MEV / MAV / MRV thresholds.
 *
 * Each row is a horizontal "frame" showing the productive band
 * (MEV→MAV in green) with markers for MV and MRV, and a pip at the
 * user's actual position. At a glance the user reads:
 *   - Which muscles are under-stimulated (pip left of MEV)
 *   - Which are in the growth band (pip inside green)
 *   - Which are over the ceiling (pip past MRV)
 *
 * Replaces the qualitative "force balance" widget for trainees who
 * care about hypertrophy programming. Force balance was a single
 * ratio; this is a per-muscle band readout.
 */

import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import OpsPanel from './OpsPanel';
import type { VolumeByMuscleSummary, MuscleVolumeReading, VolumeZone } from '@/lib/volumeByMuscle';

interface Props {
  summary: VolumeByMuscleSummary | null;
}

const MUSCLE_LABEL: Record<string, string> = {
  chest: 'Chest',
  back: 'Back',
  shoulders: 'Shoulders',
  biceps: 'Biceps',
  triceps: 'Triceps',
  quads: 'Quads',
  hamstrings: 'Hamstrings',
  glutes: 'Glutes',
  calves: 'Calves',
  abs: 'Abs',
  obliques: 'Obliques',
  core: 'Core',
  forearms: 'Forearms',
};

const ZONE_TW: Record<VolumeZone, { pip: string; label: string }> = {
  'under-mv': { pip: 'bg-red-500',    label: 'text-red-400' },
  'mv-mev':   { pip: 'bg-amber-400',  label: 'text-amber-400' },
  'mev-mav':  { pip: 'bg-emerald-400',label: 'text-emerald-400' },
  'mav-mrv':  { pip: 'bg-amber-400',  label: 'text-amber-400' },
  'over-mrv': { pip: 'bg-red-500',    label: 'text-red-400' },
};

const ZONE_BADGE: Record<VolumeZone, string> = {
  'under-mv': 'UNDER MV',
  'mv-mev':   'MV → MEV',
  'mev-mav':  'IN BAND',
  'mav-mrv':  'NEAR MRV',
  'over-mrv': 'OVER MRV',
};

export default function VolumeByMuscleCard({ summary }: Props) {
  if (!summary || summary.readings.length === 0) {
    return (
      <OpsPanel badge="Volume by Muscle" code="SYS·VOL·02" accent="amber"
        callout={<span className="text-slate-500">SPARSE</span>}>
        <p className="text-[12px] text-slate-400 leading-snug">
          No working sets logged in the last 7 days, or the exercises logged don't map to muscle groups yet. Volume per muscle uses Renaissance Periodization productive-volume thresholds (MEV–MAV is the growth band) once data is flowing.
        </p>
      </OpsPanel>
    );
  }

  const outOfBand = summary.readings.filter(r => r.zone !== 'mev-mav');
  const inBand = summary.readings.length - outOfBand.length;
  /**
   * Only UNDER MV and OVER MRV actually need action — one is losing ground,
   * the other is junk volume. "MV → MEV" (holding but not building) and
   * "NEAR MRV" (productive, watch fatigue) are normal training states.
   *
   * The callout used to count every non-mev-mav row as "OFF BAND" and paint it
   * red, so a real week showed "5 OFF BAND" in alarm red when 3 muscles were
   * in band, 3 were merely holding, and only ONE was genuinely under-stimulated.
   * The headline was scarier than the content, which is how users learn to
   * ignore a headline.
   */
  const needsWork = summary.readings.filter(r => r.zone === 'under-mv' || r.zone === 'over-mrv');
  const calloutTw = needsWork.length > 0 ? 'text-amber-400'
    : outOfBand.length === 0 ? 'text-emerald-400'
    : 'text-slate-400';

  return (
    <OpsPanel badge="Volume by Muscle" code="SYS·VOL·02" accent="amber"
      callout={
        <span className={`${calloutTw} font-bold`}>
          {needsWork.length > 0
            ? `${needsWork.length} NEEDS WORK`
            : outOfBand.length === 0
              ? 'ALL IN BAND'
              : `${inBand}/${summary.readings.length} IN BAND`}
        </span>
      }>
      <p className="font-mono text-[10px] text-slate-500 leading-relaxed mb-2">
        Weekly working sets per muscle. Green band = MEV–MAV (productive). Below MEV = no growth. Over MRV = junk volume.
      </p>

      <div className="space-y-2">
        {summary.readings.map(r => <MuscleRow key={r.muscle} reading={r} />)}
      </div>

      <div className="mt-3 pt-2 border-t border-slate-800/60 flex items-center justify-between font-mono text-[9px] text-slate-600 uppercase tracking-[0.18em] tabular-nums">
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
          <span className="text-emerald-400">{inBand} in band</span>
        </span>
        <span>{summary.sessionsAnalyzed} sessions · last 7d</span>
      </div>
    </OpsPanel>
  );
}

function MuscleRow({ reading }: { reading: MuscleVolumeReading }) {
  const t = reading.thresholds;
  const tw = ZONE_TW[reading.zone];
  // Layout the bar so MRV * 1.15 occupies the right edge — gives the
  // "over-MRV" pip somewhere visible to land without overflowing.
  const scaleMax = t.mrv * 1.15;
  const pct = (v: number) => Math.max(0, Math.min(100, (v / scaleMax) * 100));
  const pipPct = pct(reading.effectiveSets);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[11px] text-slate-200 font-semibold">{MUSCLE_LABEL[reading.muscle] ?? reading.muscle}</span>
          <span className={`font-mono text-[8px] uppercase tracking-[0.18em] font-bold ${tw.label}`}>
            {ZONE_BADGE[reading.zone]}
          </span>
        </div>
        <span className="font-mono text-[10px] text-slate-300 tabular-nums shrink-0">
          {reading.effectiveSets.toFixed(1)} sets
        </span>
      </div>

      <div className="relative h-3 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden">
        {/* MEV → MAV productive band (green) */}
        <span
          className="absolute inset-y-0 bg-emerald-500/20 border-x border-emerald-500/40"
          style={{ left: `${pct(t.mev)}%`, width: `${pct(t.mav) - pct(t.mev)}%` }}
          aria-hidden
        />
        {/* MV marker (amber dashed line) */}
        {t.mv > 0 && (
          <span aria-hidden className="absolute top-0 bottom-0 w-px bg-amber-500/40"
                style={{ left: `${pct(t.mv)}%` }} />
        )}
        {/* MRV marker (red dashed line) */}
        <span aria-hidden className="absolute top-0 bottom-0 w-px bg-red-500/60"
              style={{ left: `${pct(t.mrv)}%` }} />
        {/* Pip — user's current set count */}
        <span
          className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ${tw.pip} shadow-[0_0_4px_currentColor] -translate-x-1/2`}
          style={{ left: `${pipPct}%` }}
          aria-label={`Current ${reading.effectiveSets} sets`}
        />
      </div>

      {/* The MV/MEV/MAV/MRV label row was removed.
          It was `flex justify-between`, so the four labels landed at 0 / 33 /
          66 / 100% while the markers they name are positioned by PERCENTAGE of
          the scale above — they did not line up with their own ticks. On top of
          that: 8px type at roughly 2.7:1 contrast, four pieces of jargon per
          muscle row across thirteen rows, and "MV" is expanded nowhere in this
          card or anywhere near it.
          The pip and the plain-language hint below already say the same thing
          in words the user can act on, which is what this card is for. */}
      <p className="text-[10px] text-slate-500 mt-1 leading-snug">{reading.hint}</p>
    </div>
  );
}
